import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import './ProcessOrderStrip.scss';

export interface ProcessOrderStripItem {
    id: number;
    label: string;
}

export interface ProcessOrderStripProps {
    /** The currently-selected items in their visible order. Fully controlled. */
    selected: ProcessOrderStripItem[];
    /** Called on reorder (drag-drop, ↑↓ buttons) and on remove (× button). */
    onChange: (next: ProcessOrderStripItem[]) => void;
    /** Override the empty-state message. Defaults to a localised "no process selected yet" line. */
    emptyMessage?: React.ReactNode;
    /** Override the strip's section title. Defaults to "Rendi i prodhimit". */
    title?: React.ReactNode;
    /** Override the drag-hint text. Defaults to "Tërhiq për të ndryshuar rendin". */
    hint?: React.ReactNode;
}

/**
 * Shared admin chip-strip for showing SELECTED process IDs as an ordered,
 * draggable list with per-chip remove buttons.
 *
 * Reorder paths:
 * - Desktop (pointer: fine): native HTML5 drag-and-drop on the whole chip
 * - Touch (pointer: coarse): ↑/↓ buttons inside each chip (CSS-revealed),
 *   since HTML5 drag-and-drop does NOT fire on touch on iOS Safari /
 *   Chrome Android. The buttons are also a path for keyboard users (Tab
 *   focus → Enter), which native drag doesn't support.
 *
 * Extracted from Articles (NEW) — the original at
 * src/containers/Admin/ArticlesNew/components/ProcessOrderStrip.tsx still
 * exists and uses ChipOption ({ value, label }). This shared version uses
 * { id, label } so it slots cleanly next to the shared ProcessPicker.
 */
const ProcessOrderStrip: React.FC<ProcessOrderStripProps> = ({
    selected,
    onChange,
    emptyMessage,
    title,
    hint,
}) => {
    const intl = useIntl();
    const [dragIndex, setDragIndex] = useState<number | null>(null);
    const [overIndex, setOverIndex] = useState<number | null>(null);

    const handleDragStart = (e: React.DragEvent, index: number) => {
        setDragIndex(index);
        // Firefox needs data on the transfer to actually start the drag.
        // The effect controls the cursor in the browser-native ghost.
        e.dataTransfer.effectAllowed = 'move';
        try { e.dataTransfer.setData('text/plain', String(index)); } catch (_) { /* noop */ }
    };

    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (overIndex !== index) setOverIndex(index);
    };

    const handleDrop = (e: React.DragEvent, dropIndex: number) => {
        e.preventDefault();
        const source = dragIndex;
        setDragIndex(null);
        setOverIndex(null);
        if (source == null || source === dropIndex) return;

        const next = [...selected];
        const [moved] = next.splice(source, 1);
        next.splice(dropIndex, 0, moved);
        onChange(next);
    };

    const handleDragEnd = () => {
        setDragIndex(null);
        setOverIndex(null);
    };

    const remove = (idx: number) => {
        onChange(selected.filter((_, i) => i !== idx));
    };

    const moveUp = (idx: number) => {
        if (idx <= 0) return;
        const next = [...selected];
        [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
        onChange(next);
    };

    const moveDown = (idx: number) => {
        if (idx >= selected.length - 1) return;
        const next = [...selected];
        [next[idx + 1], next[idx]] = [next[idx], next[idx + 1]];
        onChange(next);
    };

    const moveUpLabel = intl.formatMessage({ id: 'picker.strip.moveUp', defaultMessage: 'Lëviz lart' });
    const moveDownLabel = intl.formatMessage({ id: 'picker.strip.moveDown', defaultMessage: 'Lëviz poshtë' });
    const removeLabel = intl.formatMessage({ id: 'picker.strip.remove', defaultMessage: 'Hiq' });

    if (selected.length === 0) {
        return (
            <div className="pos-strip pos-strip--empty">
                <p className="pos-strip__empty-text">
                    {emptyMessage || (
                        <FormattedMessage
                            id="picker.strip.empty"
                            defaultMessage="Asnjë proces i zgjedhur. Zgjidhni një proces më poshtë për ta shtuar."
                        />
                    )}
                </p>
            </div>
        );
    }

    return (
        <div className="pos-strip">
            <div className="pos-strip__header">
                <span className="pos-strip__title">
                    {title || <FormattedMessage id="picker.strip.title" defaultMessage="Rendi i prodhimit" />}
                </span>
                <span className="pos-strip__hint">
                    {hint || <FormattedMessage id="picker.strip.hint" defaultMessage="Tërhiq për të ndryshuar rendin" />}
                </span>
            </div>
            <div className="pos-strip__chips">
                {selected.map((item, idx) => {
                    const isDragging = dragIndex === idx;
                    const isOver = overIndex === idx && dragIndex !== idx;
                    const isFirst = idx === 0;
                    const isLast = idx === selected.length - 1;
                    return (
                        <div
                            key={item.id}
                            className={`pos-strip-chip ${isDragging ? 'is-dragging' : ''} ${isOver ? 'is-drop-target' : ''}`}
                            draggable
                            onDragStart={(e) => handleDragStart(e, idx)}
                            onDragOver={(e) => handleDragOver(e, idx)}
                            onDrop={(e) => handleDrop(e, idx)}
                            onDragEnd={handleDragEnd}
                        >
                            <span className="pos-strip-chip__order">{idx + 1}</span>
                            <span className="pos-strip-chip__label">{item.label}</span>
                            <div className="pos-strip-chip__actions">
                                {/* ↑/↓ buttons. Hidden on hover-capable desktops via CSS; shown
                                    on touch devices (pointer: coarse) and small viewports. They
                                    give touch + keyboard users a path to reorder since HTML5
                                    drag-and-drop doesn't fire on touch and isn't keyboard
                                    accessible. onMouseDown stopPropagation prevents the parent
                                    chip's dragstart from firing when the user clicks a button. */}
                                <button
                                    type="button"
                                    className="pos-strip-chip__move pos-strip-chip__move--up"
                                    aria-label={moveUpLabel}
                                    title={moveUpLabel}
                                    onClick={() => moveUp(idx)}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    disabled={isFirst}
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                        <polyline points="18 15 12 9 6 15" />
                                    </svg>
                                </button>
                                <button
                                    type="button"
                                    className="pos-strip-chip__move pos-strip-chip__move--down"
                                    aria-label={moveDownLabel}
                                    title={moveDownLabel}
                                    onClick={() => moveDown(idx)}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    disabled={isLast}
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                        <polyline points="6 9 12 15 18 9" />
                                    </svg>
                                </button>
                                <button
                                    type="button"
                                    className="pos-strip-chip__remove"
                                    aria-label={removeLabel}
                                    title={removeLabel}
                                    onClick={() => remove(idx)}
                                    onMouseDown={(e) => e.stopPropagation()}
                                >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                        <line x1="18" y1="6" x2="6" y2="18" />
                                        <line x1="6" y1="6" x2="18" y2="18" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default ProcessOrderStrip;
